import React, { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLiveEvent } from '../hooks/useLiveEvents';
import {
  isTransferStatus,
  nextTransferExpiry,
  pruneTransfers,
  upsertTransfer,
  visibleTransfers,
  type CallTransferEvent,
  type TransferNotice,
} from '@/lib/transferBanner';
import { serverNow } from '@/lib/serverClock';

/**
 * Running phone transfers (`call.transfer`) for the TopBar banner.
 *
 * Lives above the router, next to LiveEventsProvider: every page renders its
 * own Layout/TopBar, so state kept inside the TopBar would vanish on each
 * navigation — including the click on a notice that opens Verläufe.
 */
const TransferNoticesContext = createContext<TransferNotice[]>([]);

export const TransferNoticesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notices, setNotices] = useState<TransferNotice[]>([]);

  useLiveEvent<CallTransferEvent>('call.transfer', (event) => {
    // Unknown statuses are ignored: they could never reach a terminal state and auto-hide.
    if (!event?.call_sid || !isTransferStatus(event.status)) return;
    // Server clock, not the browser's: event times are the server's, and a
    // salon PC whose clock is off would otherwise drop or keep banners wrongly.
    setNotices((prev) => {
      const now = serverNow();
      return upsertTransfer(pruneTransfers(prev, now), event, now);
    });
  });

  // Hide finished transfers 60 s after their event, ringing ones after 5 min.
  useEffect(() => {
    const wait = nextTransferExpiry(notices, serverNow());
    if (wait === null) return undefined;
    const id = setTimeout(() => setNotices((prev) => pruneTransfers(prev, serverNow())), wait + 50);
    return () => clearTimeout(id);
  }, [notices]);

  const visible = useMemo(() => visibleTransfers(notices), [notices]);

  return <TransferNoticesContext.Provider value={visible}>{children}</TransferNoticesContext.Provider>;
};

/** The (at most three) transfer notices to show, newest first. */
export function useTransferNotices(): TransferNotice[] {
  return useContext(TransferNoticesContext);
}

export default TransferNoticesContext;
